import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Layers, ImageIcon, Settings2, X } from 'lucide-react';

export default function Materialliste() {
  const { API } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: '', description: '', article_number: '', manufacturer: '', unit: 'Stk.', unit_price: 0, tax_rate: 19 });
  const [photo, setPhoto] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const fileRef = useRef(null);
  const addPhotoRef = useRef(null);

  const load = async () => {
    const [m, c] = await Promise.all([axios.get(`${API}/materials`), axios.get(`${API}/materials/categories`)]);
    setMaterials(m.data);
    setCategories(c.data.length ? c.data : ['Sonstiges']);
  };

  useEffect(() => { load(); }, [API]);

  const addCategory = async () => {
    if (!newCat.trim() || categories.includes(newCat.trim())) return;
    const updated = [...categories, newCat.trim()];
    await axios.put(`${API}/materials/categories`, { categories: updated });
    setCategories(updated);
    setNewCat('');
    toast.success('Kategorie hinzugefügt');
  };

  const deleteCategory = async (cat) => {
    const updated = categories.filter(c => c !== cat);
    await axios.put(`${API}/materials/categories`, { categories: updated });
    setCategories(updated);
    toast.success('Kategorie entfernt');
  };

  const filtered = materials.filter(m => {
    if (catFilter && m.category !== catFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return m.name?.toLowerCase().includes(s) || m.article_number?.toLowerCase().includes(s) ||
        m.description?.toLowerCase().includes(s) || m.manufacturer?.toLowerCase().includes(s);
    }
    return true;
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', category: catFilter || '', description: '', article_number: '', manufacturer: '', unit: 'Stk.', unit_price: 0, tax_rate: 19 });
    setPhoto(null);
    setShowForm(true);
  };

  const openEdit = async (mat) => {
    setEditing(mat);
    setForm({ name: mat.name, category: mat.category || '', description: mat.description || '', article_number: mat.article_number || '',
      manufacturer: mat.manufacturer || '', unit: mat.unit || 'Stk.', unit_price: mat.unit_price || 0, tax_rate: mat.tax_rate || 19 });
    setPhoto(null);
    try {
      const res = await axios.get(`${API}/materials/${mat.id}/photos`);
      setExistingPhotos(res.data.photos || []);
    } catch { setExistingPhotos(mat.photos || []); }
    setShowForm(true);
  };

  const handleAddPhoto = async (e) => {
    if (!editing || !e.target.files[0]) return;
    const fd = new FormData();
    fd.append('file', e.target.files[0]);
    try {
      const res = await axios.post(`${API}/materials/${editing.id}/photos`, fd);
      setExistingPhotos(res.data.photos);
      setMaterials(prev => prev.map(m => m.id === editing.id ? { ...m, photos: res.data.photos, has_photo: true } : m));
      toast.success('Foto hinzugefügt');
    } catch { toast.error('Upload fehlgeschlagen'); }
    e.target.value = '';
  };

  const handleDeletePhoto = async (idx) => {
    if (!editing) return;
    try {
      const res = await axios.delete(`${API}/materials/${editing.id}/photos/${idx}`);
      setExistingPhotos(res.data.photos);
      setMaterials(prev => prev.map(m => m.id === editing.id ? { ...m, photos: res.data.photos, has_photo: res.data.photos.length > 0 } : m));
      toast.success('Foto gelöscht');
    } catch { toast.error('Fehler'); }
  };

  const handleSave = async () => {
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append('file', photo);
    try {
      if (editing) {
        const res = await axios.put(`${API}/materials/${editing.id}`, fd);
        setMaterials(prev => prev.map(m => m.id === editing.id ? res.data : m));
        toast.success('Aktualisiert');
      } else {
        const res = await axios.post(`${API}/materials`, fd);
        setMaterials(prev => [...prev, res.data]);
        toast.success('Material angelegt');
      }
      setShowForm(false);
      const c = await axios.get(`${API}/materials/categories`);
      setCategories([...new Set([...DEFAULT_CATEGORIES, ...c.data])]);
    } catch { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/materials/${id}`);
    setMaterials(prev => prev.filter(m => m.id !== id));
    toast.success('Gelöscht');
  };

  const grouped = {};
  filtered.forEach(m => {
    const cat = m.category || 'Sonstiges';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(m);
  });

  return (
    <div className="space-y-4 animate-fade-in" data-testid="material-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white tracking-tight">Materialkatalog</h1>
        <Button onClick={openCreate} data-testid="add-material" className="bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm shadow-[0_0_15px_rgba(245,158,11,0.3)]">
          <Plus className="w-4 h-4 mr-2" />Neues Material
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="material-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Material, Artikelnr., Hersteller suchen..." className="pl-10 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Button size="sm" variant={!catFilter ? 'default' : 'secondary'} onClick={() => setCatFilter('')}
          className={!catFilter ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}>Alle</Button>
        {categories.map(c => (
          <Button key={c} size="sm" variant={catFilter === c ? 'default' : 'secondary'} onClick={() => setCatFilter(c)}
            className={catFilter === c ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}>{c}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => setShowCatManager(true)}
          className="text-slate-500 hover:text-amber-400"><Settings2 className="w-3.5 h-3.5" /></Button>
      </div>

      {showCatManager && (
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white">Kategorien verwalten</h3>
            <button onClick={() => setShowCatManager(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2">
            <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Neue Kategorie..."
              onKeyDown={e => e.key === 'Enter' && addCategory()}
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 text-sm flex-1" />
            <Button size="sm" onClick={addCategory} className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-sm"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map(c => (
              <span key={c} className="flex items-center gap-1 px-2 py-1 bg-slate-800 text-slate-300 rounded-sm text-xs">
                {c}
                <button onClick={() => deleteCategory(c)} className="text-slate-500 hover:text-rose-400 ml-1"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}

      {Object.keys(grouped).length ? Object.entries(grouped).map(([cat, mats]) => (
        <div key={cat}>
          <div className="flex items-center gap-2 mb-2 mt-4">
            <Layers className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{cat}</h3>
            <span className="text-xs font-mono text-slate-600">{mats.length}</span>
          </div>
          <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
            {mats.map(m => (
              <div key={m.id} className="flex items-center gap-4 px-4 py-3 border-b border-slate-800/40 hover:bg-slate-900/80 transition-colors group"
                data-testid={`mat-${m.id}`}>
                <div className="w-10 h-10 rounded-sm bg-slate-800 border border-slate-700 overflow-hidden flex items-center justify-center shrink-0">
                  {m.has_photo ? (
                    <img src={`${API}/materials/${m.id}/photo`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Layers className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-200 truncate">{m.name}</p>
                    {m.article_number && <span className="text-[10px] font-mono text-slate-600">Art. {m.article_number}</span>}
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {m.manufacturer && <span>{m.manufacturer} · </span>}
                    {m.description}
                  </p>
                </div>
                <span className="text-sm font-mono text-amber-400 shrink-0">{m.unit_price?.toFixed(2)} EUR / {m.unit}</span>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(m)} className="text-slate-400 hover:text-amber-400 w-8 h-8"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(m.id)} className="text-slate-400 hover:text-rose-400 w-8 h-8"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )) : <div className="p-8 text-center text-slate-500 bg-slate-900/50 border border-slate-800/60 rounded-sm">{search || catFilter ? 'Keine Materialien gefunden' : 'Noch keine Materialien im Katalog'}</div>}

      <Dialog open={showForm} onOpenChange={v => { setShowForm(v); if (!v) setEditing(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="text-white">{editing ? 'Material bearbeiten' : 'Neues Material'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Photo gallery */}
            <div>
              <Label className="text-slate-300 text-xs mb-2 block">Fotos</Label>
              <div className="flex gap-2 flex-wrap">
                {/* Existing photos (when editing) */}
                {editing && existingPhotos.map((_, idx) => (
                  <div key={idx} className="relative w-16 h-16 rounded-sm bg-slate-950 border border-slate-800 overflow-hidden group">
                    <img src={`${API}/materials/${editing.id}/photo?idx=${idx}`} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => handleDeletePhoto(idx)}
                      className="absolute top-0 right-0 w-5 h-5 bg-rose-500 rounded-bl-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                {/* New photo preview (for create) */}
                {photo && !editing && (
                  <div className="w-16 h-16 rounded-sm bg-slate-950 border border-slate-800 overflow-hidden">
                    <img src={URL.createObjectURL(photo)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {/* Add photo button */}
                {editing ? (
                  <button onClick={() => addPhotoRef.current?.click()}
                    className="w-16 h-16 rounded-sm border-2 border-dashed border-slate-700 flex flex-col items-center justify-center text-slate-600 hover:border-amber-500/50 hover:text-amber-400 transition-colors">
                    <Plus className="w-5 h-5" /><span className="text-[8px]">Foto</span>
                  </button>
                ) : (
                  <button onClick={() => fileRef.current?.click()}
                    className="w-16 h-16 rounded-sm border-2 border-dashed border-slate-700 flex flex-col items-center justify-center text-slate-600 hover:border-amber-500/50 hover:text-amber-400 transition-colors">
                    <ImageIcon className="w-5 h-5" /><span className="text-[8px]">Foto</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => setPhoto(e.target.files[0])} />
                <input ref={addPhotoRef} type="file" accept="image/*" className="hidden" onChange={handleAddPhoto} />
              </div>
              <p className="text-[9px] text-slate-600 mt-1">Mehrere Fotos möglich. Alle werden in die PDF-Materialliste übernommen.</p>
            </div>
            <div className="space-y-2">
                <div><Label className="text-slate-300 text-xs">Bezeichnung</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. Rigipsplatte 12,5mm"
                  className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-slate-300 text-xs">Kategorie</Label>
                  <Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="Trockenbau"
                    className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" list="cat-list" />
                  <datalist id="cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist></div>
                  <div><Label className="text-slate-300 text-xs">Artikelnr.</Label>
                  <Input value={form.article_number} onChange={e => setForm({...form, article_number: e.target.value})}
                    className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" /></div>
                </div>
            </div>
            <div><Label className="text-slate-300 text-xs">Hersteller</Label>
            <Input value={form.manufacturer} onChange={e => setForm({...form, manufacturer: e.target.value})} placeholder="z.B. Knauf, Rigips"
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" /></div>
            <div><Label className="text-slate-300 text-xs">Beschreibung</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-slate-300 text-xs">Preis (EUR)</Label>
              <Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({...form, unit_price: parseFloat(e.target.value)||0})}
                className="bg-slate-950 border-slate-800 text-slate-200" /></div>
              <div><Label className="text-slate-300 text-xs">Einheit</Label>
              <Input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}
                className="bg-slate-950 border-slate-800 text-slate-200" /></div>
              <div><Label className="text-slate-300 text-xs">MwSt. %</Label>
              <Input type="number" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: parseFloat(e.target.value)||19})}
                className="bg-slate-950 border-slate-800 text-slate-200" /></div>
            </div>
            <Button onClick={handleSave} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {editing ? 'Speichern' : 'Material anlegen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
